import type {
  AdminEnvelope,
  AdminOrganization,
  AdminOrganizationCreatePayload,
  AdminOrganizationDetail,
  AdminOrganizationSearchablePayload,
  AdminOrganizationTransferOwnershipPayload,
  AdminOrganizationUpdatePayload,
  AdminOrganizationVatStatusPayload,
  AdminOrgRole,
  AdminOrgRoleCreatePayload,
  AdminOrgRolePermissionToggleResponse,
  AdminOrgRolePermissionsSetPayload,
  AdminOrgRolePermissionsSetResponse,
  AdminOrgRoleUpdatePayload,
} from '@/client/types/admin/users-orgs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useGetMutationFn from '../use-get-mutation-fn';
import { toFormData } from './lib/form';
import { adminOrgKeys } from './organizations.query';

export const useCreateOrganizationMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<FormData, AdminEnvelope<AdminOrganization>>({
    defaultErrorMessage:
      'An unexpected error occurred while creating the organization',
    endpoint: '/api/admin/organizations',
    method: 'POST',
  });

  return useMutation({
    mutationFn: (payload: AdminOrganizationCreatePayload) =>
      mutationFn(toFormData(payload as unknown as Record<string, unknown>)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminOrgKeys.all });
    },
  });
};

export const useUpdateOrganizationMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    AdminOrganizationUpdatePayload,
    AdminEnvelope<AdminOrganization>
  >({
    defaultErrorMessage:
      'An unexpected error occurred while updating the organization',
    endpoint: (payload) => `/api/admin/organizations/${payload.oid}`,
    method: 'PUT',
  });

  return useMutation({
    mutationFn,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: adminOrgKeys.all });
      queryClient.invalidateQueries({
        queryKey: adminOrgKeys.detail(variables.oid),
      });
    },
  });
};

export type OrgLifecycleAction =
  | 'activate'
  | 'deactivate'
  | 'delete'
  | 'undelete';

export const useOrganizationLifecycleMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    { oid: string | number; action: OrgLifecycleAction },
    AdminEnvelope<AdminOrganization>
  >({
    defaultErrorMessage:
      'An unexpected error occurred while updating the organization',
    endpoint: (payload) =>
      `/api/admin/organizations/${payload.action}/${payload.oid}`,
    method: 'PUT',
  });

  return useMutation({
    mutationFn,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: adminOrgKeys.all });
      queryClient.invalidateQueries({
        queryKey: adminOrgKeys.detail(variables.oid),
      });
    },
  });
};

export const useOrganizationHoldMutation = () => {
  const queryClient = useQueryClient();

  const setHold = useGetMutationFn<
    { oid: string | number; reason?: string },
    AdminEnvelope<AdminOrganization>
  >({
    defaultErrorMessage: 'An unexpected error occurred while placing the hold',
    endpoint: (payload) => `/api/admin/organizations/${payload.oid}/hold`,
    method: 'PUT',
  });

  const liftHold = useGetMutationFn<
    { oid: string | number; reason?: string },
    AdminEnvelope<AdminOrganization>
  >({
    defaultErrorMessage: 'An unexpected error occurred while lifting the hold',
    endpoint: (payload) => `/api/admin/organizations/${payload.oid}/hold`,
    method: 'DELETE',
  });

  return useMutation({
    mutationFn: ({
      oid,
      hold,
      reason,
    }: {
      oid: string | number;
      hold: boolean;
      reason?: string;
    }) => (hold ? setHold({ oid, reason }) : liftHold({ oid, reason })),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: adminOrgKeys.all });
      queryClient.invalidateQueries({
        queryKey: adminOrgKeys.detail(variables.oid),
      });
    },
  });
};

/**
 * Moderation decision.
 *
 * Approve → `review=0`, org becomes publicly listable.
 * Reject  → stays `review=1` with `review_reason` recorded.
 *
 * Rejecting does NOT bar the org from transacting — that is `admin_hold`.
 */
export const useOrgReviewDecisionMutation = () => {
  const queryClient = useQueryClient();

  const approve = useGetMutationFn<
    { oid: string | number },
    AdminEnvelope<AdminOrganization>
  >({
    defaultErrorMessage:
      'An unexpected error occurred while approving the organization',
    endpoint: (payload) =>
      `/api/admin/organizations/${payload.oid}/review/approve`,
    method: 'PUT',
  });

  const reject = useGetMutationFn<
    { oid: string | number; reason?: string },
    AdminEnvelope<AdminOrganization>
  >({
    defaultErrorMessage:
      'An unexpected error occurred while rejecting the organization',
    endpoint: (payload) =>
      `/api/admin/organizations/${payload.oid}/review/reject`,
    method: 'PUT',
  });

  return useMutation({
    mutationFn: ({
      oid,
      approved,
      reason,
    }: {
      oid: string | number;
      approved: boolean;
      reason?: string;
    }) => (approved ? approve({ oid }) : reject({ oid, reason })),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: adminOrgKeys.all });
      queryClient.invalidateQueries({
        queryKey: adminOrgKeys.detail(variables.oid),
      });
    },
  });
};

export const useOrganizationVatStatusMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    AdminOrganizationVatStatusPayload,
    AdminEnvelope<AdminOrganizationDetail>
  >({
    defaultErrorMessage:
      'An unexpected error occurred while updating VAT status',
    endpoint: (payload) =>
      `/api/admin/organizations/${payload.oid}/vat-status`,
    method: 'PUT',
  });

  return useMutation({
    mutationFn,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: adminOrgKeys.all });
      queryClient.invalidateQueries({
        queryKey: adminOrgKeys.detail(variables.oid),
      });
    },
  });
};

export const useOrganizationTransferOwnershipMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    AdminOrganizationTransferOwnershipPayload,
    AdminEnvelope<AdminOrganizationDetail>
  >({
    defaultErrorMessage:
      'An unexpected error occurred while transferring ownership',
    endpoint: (payload) =>
      `/api/admin/organizations/${payload.oid}/transfer-ownership`,
    method: 'POST',
  });

  return useMutation({
    mutationFn,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: adminOrgKeys.all });
      queryClient.invalidateQueries({
        queryKey: adminOrgKeys.detail(variables.oid),
      });
    },
  });
};

export const useOrganizationSearchableMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    AdminOrganizationSearchablePayload,
    AdminEnvelope<AdminOrganization>
  >({
    defaultErrorMessage:
      'An unexpected error occurred while updating directory visibility',
    endpoint: (payload) =>
      `/api/admin/organizations/${payload.oid}/searchable`,
    method: 'PUT',
  });

  return useMutation({
    mutationFn,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: adminOrgKeys.all });
      queryClient.invalidateQueries({
        queryKey: adminOrgKeys.detail(variables.oid),
      });
    },
  });
};

/* ────────────────── Org role catalogue (global) ────────────────── */

export const useCreateOrgRoleMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    AdminOrgRoleCreatePayload,
    AdminEnvelope<AdminOrgRole>
  >({
    defaultErrorMessage:
      'An unexpected error occurred while creating the organization role',
    endpoint: '/api/admin/organizations/member-roles',
    method: 'POST',
  });

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminOrgKeys.all });
    },
  });
};

export const useUpdateOrgRoleMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    AdminOrgRoleUpdatePayload,
    AdminEnvelope<AdminOrgRole>
  >({
    defaultErrorMessage:
      'An unexpected error occurred while updating the organization role',
    endpoint: (payload) =>
      `/api/admin/organizations/member-roles/${payload.ourid}`,
    method: 'PUT',
  });

  return useMutation({
    mutationFn,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: adminOrgKeys.all });
      queryClient.invalidateQueries({
        queryKey: adminOrgKeys.roleDetail(variables.ourid),
      });
    },
  });
};

export const useDeleteOrgRoleMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    { ourid: number },
    AdminEnvelope<{ success?: boolean }>
  >({
    defaultErrorMessage:
      'An unexpected error occurred while deleting the organization role',
    endpoint: (payload) =>
      `/api/admin/organizations/member-roles/${payload.ourid}`,
    method: 'DELETE',
  });

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminOrgKeys.all });
    },
  });
};

export const useSetOrgRolePermissionsMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    AdminOrgRolePermissionsSetPayload,
    AdminEnvelope<AdminOrgRolePermissionsSetResponse>
  >({
    defaultErrorMessage:
      'An unexpected error occurred while updating role permissions',
    endpoint: (payload) =>
      `/api/admin/organizations/member-roles/${payload.ourid}/permissions`,
    method: 'PUT',
  });

  return useMutation({
    mutationFn,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: adminOrgKeys.all });
      queryClient.invalidateQueries({
        queryKey: adminOrgKeys.roleDetail(variables.ourid),
      });
    },
  });
};

export const useToggleOrgRolePermissionMutation = () => {
  const queryClient = useQueryClient();

  const grant = useGetMutationFn<
    { ourid: number; code: string },
    AdminEnvelope<AdminOrgRolePermissionToggleResponse>
  >({
    defaultErrorMessage:
      'An unexpected error occurred while granting the permission',
    endpoint: (payload) =>
      `/api/admin/organizations/member-roles/${payload.ourid}/permissions/${encodeURIComponent(payload.code)}`,
    method: 'POST',
  });

  const revoke = useGetMutationFn<
    { ourid: number; code: string },
    AdminEnvelope<AdminOrgRolePermissionToggleResponse>
  >({
    defaultErrorMessage:
      'An unexpected error occurred while revoking the permission',
    endpoint: (payload) =>
      `/api/admin/organizations/member-roles/${payload.ourid}/permissions/${encodeURIComponent(payload.code)}`,
    method: 'DELETE',
  });

  return useMutation({
    mutationFn: ({
      ourid,
      code,
      grant: shouldGrant,
    }: {
      ourid: number;
      code: string;
      grant: boolean;
    }) =>
      shouldGrant ? grant({ ourid, code }) : revoke({ ourid, code }),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: adminOrgKeys.all });
      queryClient.invalidateQueries({
        queryKey: adminOrgKeys.roleDetail(variables.ourid),
      });
    },
  });
};
